import { useEffect, useMemo, useRef, useState } from "react";
import { Stepper } from "../components/Stepper";
import { Views, FormData } from "../types";
import { conditionsByAdverseActionLetterType } from "../constants";
import { Button, ButtonVariant, Show, TextArea } from "shared/components";

import { ThumbsDown, ThumbsUp } from "untitledui-js-base";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";
import { Applicant, DecisionAction } from "shared/types/application";
import { FEATURES } from "types/landlord/partner";
import { Property } from "shared/types";
import { generateAdditionalEmailText } from "../utils";
import { AdverseActionLetterType } from "api/landlord/templates";
import { Template } from "types/landlord/verificationTemplate";

export const EmailView = ({
  formData,
  updateFormData,
  view,
  flow,
  address,
  applicants,
  property,
  verificationTemplate,
  conditionalApprovalEmailMessage,
  approvalEmailMessage,
  declinedEmailMessage
}: {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
  view: Views;
  flow: Views[];
  address: string;
  applicants: Applicant[];
  property?: Property;
  verificationTemplate?: Template;
  conditionalApprovalEmailMessage?: string | null;
  approvalEmailMessage?: string | null;
  declinedEmailMessage?: string | null;
}) => {
  const [editMode, setEditMode] = useState(false);
  const { partner } = useAuthContext();
  const [additionalText, setAdditionalText] = useState<string>("");

  const letterType =
    formData.decision === DecisionAction.Approve
      ? AdverseActionLetterType.Generic
      : verificationTemplate?.adverse_action_letter_type ||
        AdverseActionLetterType.Generic;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const partnerNameInEmail = partner?.features.includes(
    FEATURES.USE_PROPERTY_NAME_IN_EMAIL
  )
    ? property?.name
    : partner?.name;

  const applicationConditions = conditionsByAdverseActionLetterType[letterType];

  const decisionLabel = useMemo(() => {
    if (formData.decision === DecisionAction.Decline) return "declined";
    if (formData.decision === DecisionAction.ConditionalApprove)
      return "conditionally approved";
    return "approved";
  }, [formData.decision]);

  useEffect(() => {
    const text = generateAdditionalEmailText(
      formData,
      applicants,
      address,
      applicationConditions,
      letterType,
      conditionalApprovalEmailMessage,
      approvalEmailMessage,
      declinedEmailMessage
    );

    updateFormData({ additionalEmailText: text });
    setAdditionalText(text);
  }, [formData.decision, formData.residencyConditions, formData.conditions]);

  return (
    <div className="flex flex-col h-full">
      <Stepper view={view} flow={flow} />

      <div className="p-6 min-h-full">
        <div className="min-h-full bg-background-900 border border-alpha-100 rounded-lg p-4 md:p-8">
          <div className="mx-auto max-w-[640px] w-full bg-white border border-alpha-100 shadow-sm px-3 md:px-12 py-10 rounded-lg">
            {formData.decision === DecisionAction.Decline ? (
              <ThumbsDown className="h-[50px] w-[50px] stroke-danger-500" />
            ) : (
              <ThumbsUp
                className={cx(
                  "h-[50px] w-[50px]",
                  formData.decision === DecisionAction.ConditionalApprove
                    ? "stroke-warning-500"
                    : "stroke-success-500"
                )}
              />
            )}

            <h2 className="my-6 text-2xl font-bold text-content-1000">
              Application {decisionLabel}
            </h2>

            <p className="text-base text-content-500 mb-2">
              {/* Not sure if needs to be changed to getFullName */}
              Dear {applicants[0]?.first_name} {applicants[0]?.last_name},
            </p>

            <p className="text-base text-content-500 border-b border-alpha-40 pb-6 mb-6">
              {partnerNameInEmail} has {decisionLabel} your application for{" "}
              <span className="font-bold text-content-500">{address}.</span>
            </p>

            <div className="border-b border-alpha-40 pb-6 mb-6">
              {editMode ? (
                <div>
                  <TextArea
                    id="additionalText"
                    ref={textAreaRef}
                    label={`Message from ${partnerNameInEmail}`}
                    value={additionalText}
                    onChange={(e) => setAdditionalText(e.target.value)}
                    className="whitespace-pre-line"
                    placeholder="Enter additional text here"
                    rows={5}
                  />
                  <div className="flex gap-2 items-center justify-end">
                    <Button
                      onClick={() => {
                        if (textAreaRef.current)
                          textAreaRef.current.value =
                            formData.additionalEmailText;

                        setAdditionalText(formData.additionalEmailText);
                        setEditMode(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      onClick={() => {
                        updateFormData({ additionalEmailText: additionalText });
                        setEditMode(false);
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="whitespace-pre-line p-5 rounded-lg flex gap-2 bg-background-900 border border-alpha-100">
                  <p className="text-sm text-content-500">
                    <p className="font-bold mb-2">
                      Message from {partnerNameInEmail}:
                    </p>

                    <p>{additionalText}</p>
                    <Show
                      if={
                        letterType === AdverseActionLetterType.Washington &&
                        verificationTemplate?.adverse_action_letter_type ===
                          AdverseActionLetterType.Washington
                      }
                    >
                      <p>Agent/Owner Signature:</p>
                      {verificationTemplate?.adverse_action_letter_signature
                        ?.svg &&
                        typeof verificationTemplate
                          .adverse_action_letter_signature.svg === "object" &&
                        verificationTemplate.adverse_action_letter_signature.svg
                          .asset?.url && (
                          <img
                            src={
                              verificationTemplate
                                .adverse_action_letter_signature.svg.asset.url
                            }
                            alt="Adverse Action Letter"
                            className="text-sm text-content-500 h-[100px]"
                          />
                        )}
                      {verificationTemplate?.adverse_action_letter_address && (
                        <div className="mt-2 text-sm text-content-500">
                          <p>{partner?.name}</p>
                          <p>
                            {
                              verificationTemplate.adverse_action_letter_address
                                .street_address
                            }
                          </p>
                          {verificationTemplate.adverse_action_letter_address
                            .apartment_address && (
                            <p>
                              {
                                verificationTemplate
                                  .adverse_action_letter_address
                                  .apartment_address
                              }
                            </p>
                          )}
                          <p>
                            {
                              verificationTemplate.adverse_action_letter_address
                                .city
                            }
                            ,{" "}
                            {
                              verificationTemplate.adverse_action_letter_address
                                .state
                            }{" "}
                            {
                              verificationTemplate.adverse_action_letter_address
                                .postal_code
                            }
                          </p>
                        </div>
                      )}
                    </Show>
                  </p>
                  <Button
                    className="shrink-0"
                    onClick={() => {
                      setEditMode(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </div>

            {formData.decision !== DecisionAction.Approve ? (
              <div className="border-b border-alpha-40 pb-6 mb-6">
                <p className="text-sm text-content-500 whitespace-pre-line">
                  {applicants[0]?.disclosures}
                </p>
              </div>
            ) : null}

            <p className="text-sm text-content-500 ">Regards, </p>
            <p className="text-sm text-content-500 font-bold mb-4">Team Boom</p>

            <p className="text-sm text-content-300">
              You are receiving this email because {partnerNameInEmail} is using
              Boom to power their application experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
